import { useContext, useState, useEffect, useRef } from "react";
import { PopupComponent } from "components/popup/popup";
import PopupContent from "components/shared/popup-content";
import { ChannelPopup } from "components/channel-popup/channel-popup";
import { channelDataAndProgrammeArrSet } from "core/lib/list-methods";
import EmptyListItemComponent from "components/list/empty-list-item";
import { getAnalyticTags, getChannelTag } from "core/lib/analytics-methods";
import context from "core/tv-guide.context";
import { observer } from "mobx-react-lite";
import { AppContext } from "core/providers/tv-guide.context";
import { ChannelData, EmptyItem, PopupInfo, ProgrammesAndDate } from "core/types";
import ListForDate from "./list/list-for-date";
import Loader from "./loader/loader";

const TVGuideList = observer(() => {
  const appContext = useContext(AppContext);
  const store = appContext.store;

  const [showPopup, setShowPopup] = useState(false);
  const [, setPackage] = useState("");

  const [loader, setLoader] = useState(true);

  const [currentChannelData, setCurrentChannelData] = useState<ChannelData | undefined>(undefined);
  const [grabbing, setGrabbing] = useState(false);
  const [programmes, setProgrammes] = useState<ProgrammesAndDate[]>([]);
  const [emptyArr, setEmptyArr] = useState<EmptyItem[] | undefined>(undefined);
  const [popupInfo, setPopupInfo] = useState<PopupInfo | undefined>(undefined);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const listMain = useRef<any>(null);

  const dictionary = context.dictionary;

  const popupCallback = () => {
    setPopupInfo(undefined);
    setShowPopup(false);
  }

  useEffect(() => {
    const options = { parentClassName: "listBody", targetClassName: "open-popup" };
    ChannelPopup.Create(store, options);
  }, []);

  useEffect(() => {
    if (!store.currentChannel) {
      return;
    }

    // Check if the current channel exists in current channel list
    store.updateCurrentChannel(store.currentChannel);

    if (!store.channelNumbers?.includes(store.currentChannel)) {
      store.updateCurrentChannel(store.channelNumbers[0]);
    }

    setCurrentChannelData(undefined);

    channelDataAndProgrammeArrSet(
      store,
      store.dateOption,
      setCurrentChannelData,
      setProgrammes,
      setEmptyArr
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    store.currentChannel,
    store.dateOption
  ]);

  useEffect(() => {
    const extendDates = async () => {
      await appContext.extendDays(parseInt(store.dateOption));

      channelDataAndProgrammeArrSet(
        store,
        store.dateOption,
        setCurrentChannelData,
        setProgrammes,
        setEmptyArr
      );
    }

    extendDates();
  }, [store.dateOption]);

  useEffect(() => {
    setLoader(!store.hasBasicData || !store.hasScheduleData);

    setPackage(store.searchCriteria.package ?? "");

    setLoader(currentChannelData === undefined);

  }, [store.hasScheduleData, store.isFetching, currentChannelData, store.dateOption]);

  // Debounce
  let timeout: NodeJS.Timeout;

  useEffect(() => {
    const delayed = () => {
      setLoader(false);
    }

    if (timeout) {
      clearTimeout(timeout);
    } else {
      setLoader(false);
    }

    timeout = setTimeout(delayed, 500);
  }, [store]);

  const onQuickGenreLinkClickHandle = (item: string) => {
    const genre = store.genres.find(x => x.mainGenre === item);
    const genreValue = (genre?.value || genre?.mainGenre) ?? "";
    appContext.updateGenre(genreValue);
  }

  if (loader) {
    return (
      <section>
        <div className="list-body" id='listBody'>
          <Loader />
        </div>
      </section>
    );
  }

  const ChannelLogo = () => {
    if (!programmes.length) {
      return null;
    }

    const data = currentChannelData as ChannelData;

    const dataTag = getChannelTag(data);

    return (<div className="list-logo">
      <p>
        {store.currentChannel}
      </p>
      <div className="channel-logo open-popup"  {...dataTag}>
        {data.imgSrc && (
          <img
            alt={data.tag}
            className="img-placeholder"
            src={data.imgSrc}
          />
        )}
      </div>
    </div>);
  }

  const fifaAnalytics = () => {
    return getAnalyticTags({
      component_name: "fifa_quick_link_genre_selected",
      component_type: "genre_selected",
      genre_type: "FIFA World Cup"
    });
  }

  return (
    <section>
      <div className="list-body" id='listBody'>
        {
          context.fifaWorldCup && (
            <div className='fifa-world-cup-quick-link quick-link-list-view'>
              <p>
                {dictionary.fifaWorldCupLinkText}<span {...fifaAnalytics()}
                  onClick={() => onQuickGenreLinkClickHandle("FIFA World Cup")}>{` ${dictionary.clickHere}`}</span>
              </p>
            </div>
          )
        }
        <div
          className="channel-wrap grabbable"
          ref={listMain}
          onMouseDown={() => {
            if (!grabbing) {
              setGrabbing(true);
            }
          }}
          onMouseMove={(ev) => {
            if (grabbing) {
              listMain.current.scrollBy(-ev.movementX, -ev.movementY);
            }
          }}
          onMouseLeave={() => {
            if (grabbing) {
              setGrabbing(false);
            }
          }}
          onMouseUp={() => {
            if (grabbing) {
              setGrabbing(false);
            }
          }}>
          <ChannelLogo />

          {programmes && programmes.map((item) =>
            <ListForDate
              key={item.date}
              date={item.date}
              programmes={item.programmes}
              setShowPopup={setShowPopup}
              setPopupInfo={setPopupInfo} />
          )}

          {(programmes === undefined || store.isFetching) && (
            <EmptyListItemComponent
              emptyArr={emptyArr}
              fetching={true} />
          )}
        </div>
      </div>

      <PopupComponent showPopup={showPopup} popupCallback={popupCallback}>
        <PopupContent popupInfo={popupInfo} channelNumber={store.currentChannel} />
      </PopupComponent>
    </section>
  );
});

export default TVGuideList;
